import React, { Fragment, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronDown, ChevronUp } from 'lucide-react';

import { Member } from '@/models/AudienceByWorkspace';
import { Campaign } from '@/models/Campaigns';
import { Domain } from '@/models/Domain';
import { Notification } from '@/models/NotificationsByWorkspace';
import { Segment } from '@/models/Segments';
import { Template } from '@/models/TemplatesByTenant';
import { AppRoutes } from '@/utils/routes/router';

import PaginationControls from '../Pagination/PaginationControls';

import { TableProps } from './types';

const Table: <T>(props: TableProps<T>) => React.ReactElement<TableProps<T>> = ({
  columns,
  data,
  sortable = false,
  serverPagination,
  serverSorting,
  caption,
  className,
  headerStyle,
  showPagination = true,
  showRecordsPerPage = true,
  fullWidth = true,
  onRowClick,
}) => {
  const [clientSorting, setClientSorting] = useState<SortingState>([]);
  const [clientPagination, setClientPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 15 });

  const serverSortMode = serverSorting != null;
  const sortingState = serverSortMode ? serverSorting.sorting : clientSorting;
  const onSortingChange = serverSortMode ? serverSorting.onSortingChange : setClientSorting;

  const serverMode = serverPagination != null;
  const paginationState = serverMode
    ? { pageIndex: serverPagination.pageIndex, pageSize: serverPagination.pageSize }
    : clientPagination;

  const pageCount = serverMode
    ? Math.max(1, Math.ceil(serverPagination.totalRowCount / Math.max(1, serverPagination.pageSize)))
    : undefined;

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: serverMode,
    manualSorting: sortable && serverSortMode,
    pageCount,
    onPaginationChange: serverMode ? serverPagination.onPaginationChange : setClientPagination,
    ...(sortable && !serverSortMode
      ? {
          getSortedRowModel: getSortedRowModel(),
          onSortingChange,
        }
      : sortable && serverSortMode
        ? { onSortingChange }
        : {}),
    ...(!serverMode ? { getPaginationRowModel: getPaginationRowModel() } : {}),
    state: sortable
      ? { sorting: sortingState, pagination: paginationState }
      : { pagination: paginationState },
  });
  const navigate = useNavigate();
  const isValueAMember = (value: any): value is Member => !!value?.id && value?.entityType === 'Audience';
  const isValueASegment = (value: any): value is Segment => value?.entityType === 'Segment';
  const isValueADelivery = (value: any): value is Segment => value?.entityType === 'Scheduled-Notifications';
  const isValueADomain = (value: any): value is Domain => value?.entityType === 'Domain';
  const isValueACampaign = (value: any): value is Campaign => value?.entityType === 'Campaign';
  const isValueATemplate = (value: any): value is Template => value?.entityType === 'Template';

  const currentPageIndex = table.getState().pagination.pageIndex;
  const rows = table.getPaginationRowModel().rows;
  const lastTableRow = rows[rows.length - 1];
  const firstCell = lastTableRow.getVisibleCells()[0];
  const lastCell = lastTableRow.getVisibleCells()[lastTableRow.getVisibleCells().length - 1];

  return (
    <>
      <div className="relative overflow-auto">
        <table data-cy="table" className={`table-auto ${fullWidth && 'w-full'} border-collapse ${className?.table}`}>
          <caption className="text-left">{caption}</caption>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = sortable && header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      aria-sort={
                        !canSort
                          ? undefined
                          : sorted === 'asc'
                            ? 'ascending'
                            : sorted === 'desc'
                              ? 'descending'
                              : 'none'
                      }
                      className={`border-b border-custom-lightSilver px-4 py-2 text-left text-sm text-custom-darkSilver font-medium min-w-[80px] truncate ${
                        className?.header ?? ''
                      }`}
                      style={{ ...headerStyle, width: `${header.getSize?.() ?? header.column.getSize?.() ?? 0}px` }}
                    >
                      {header.isPlaceholder ? null : canSort ? (
                        <button
                          type="button"
                          className="inline-flex max-w-full items-center gap-1.5 text-left font-medium text-custom-darkSilver hover:text-custom-black"
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' ? (
                            <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
                          )}
                        </button>
                      ) : (
                        flexRender(header.column.columnDef.header, header.getContext())
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, i) => (
              <Fragment key={i}>
                <tr
                  key={row.id}
                  className={`group hover:cursor-pointer text-sm hover:bg-custom-blue/5 h-10 ${className?.row ?? ''}`}
                  data-testid="table-row"
                  onClick={() => {
                    if (onRowClick) {
                      onRowClick(row.original);
                      return;
                    }
                    if (isValueAMember(row.original)) {
                      return navigate(generatePath(AppRoutes.memberProfile, { id: (row.original as Member).id }));
                    } else if (isValueASegment(row.original)) {
                      return navigate(generatePath(AppRoutes.segmentOverview, { id: (row.original as Segment).id }));
                    } else if (isValueADomain(row.original)) {
                      return navigate(
                        generatePath(AppRoutes.domainOverview, { domainName: (row.original as Domain).domainName }),
                      );
                    } else if (isValueADelivery(row.original)) {
                      return navigate(
                        generatePath(AppRoutes.deliveryOverview, { id: (row.original as unknown as Notification).id }),
                      );
                    } else if (isValueACampaign(row.original)) {
                      return navigate(
                        generatePath(AppRoutes.campaignOverview, { id: (row.original as unknown as Campaign).id }),
                      );
                    } else if (isValueATemplate(row.original)) {
                      return navigate(
                        generatePath(AppRoutes.templateOverview, { id: (row.original as unknown as Template).id }),
                        {
                          state: {
                            template: row.original as unknown as Template,
                          },
                        },
                      );
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell, i) => (
                    <td
                      key={cell.id}
                      className={`px-4 group-hover:bg-custom-blue/5 group-hover:rounded-none min-w-[80px] truncate text-custom-darkSilver bg-white ${
                        className?.cell ?? ''
                      }`}
                      style={{ width: `${cell.column.getSize?.() ?? 0}px` }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>
      {showPagination && (
        <PaginationControls
          table={table}
          currentPageIndex={currentPageIndex}
          data={data}
          totalRowCount={serverMode ? serverPagination.totalRowCount : undefined}
          showRecordsPerPage={showRecordsPerPage}
          className={className?.pagination}
        />
      )}
    </>
  );
};

export default Table;
